import React, { useState, useEffect, useCallback, useRef } from "react";
import steamService from "../../service/steam.service";
import backendApiService from "../../service/backend-api.service";
import SBPPaymentPage from "./SBPPaymentPage";

const SteamTopUpWidget = () => {
  const [steamAccount, setSteamAccount] = useState("");
  const [amountUSD, setAmountUSD] = useState(5);
  const [selectedRegion, setSelectedRegion] = useState("RUB");
  const [currencyRates, setCurrencyRates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountValidation, setAccountValidation] = useState(null);
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  const [localAmount, setLocalAmount] = useState(0);
  const [rubAmount, setRubAmount] = useState(0);
  const [step, setStep] = useState(1); // 1 - основной интерфейс, 2 - оплата, 3 - страница СБП
  const [orderInfo, setOrderInfo] = useState(null);
  const [markupPercentage, setMarkupPercentage] = useState(10); // Процент наценки с бэкенда
  const [accountCurrencyAmount, setAccountCurrencyAmount] = useState(0); // Сумма в валюте аккаунта (рубли/тенге)
  const [isCryptoPayment, setIsCryptoPayment] = useState(false); // Переключатель крипто/рубли
  const [cryptoAmount, setCryptoAmount] = useState(0); // Сумма в USDT для криптооплаты
  const [cryptoCommissionPercentage, setCryptoCommissionPercentage] = useState(4); // Процент криптокомиссии
  
  // Убрали кэширование для мгновенных обновлений

  // Функция конвертации из валюты аккаунта в USD с округлением в большую сторону
  const convertAccountCurrencyToUSD = (amount, currency) => {
    if (!currencyRates || !currencyRates[currency] || amount <= 0) {
      return 0;
    }
    
    // Конвертируем в USD
    const usdAmount = amount / currencyRates[currency];
    
    // Округляем в большую сторону до сотых доллара
    return Math.ceil(usdAmount * 100) / 100;
  };

  // Функция конвертации из USD в валюту аккаунта
  const convertUSDToAccountCurrency = (usdAmount, currency) => {
    if (!currencyRates || !currencyRates[currency] || usdAmount <= 0) {
      return 0;
    }
    
    return usdAmount * currencyRates[currency];
  };

  // Функция загрузки криптокомиссии с бэкенда
  const loadCryptoCommissionSettings = useCallback(async () => {
    try {
      console.log('💰 Загружаем криптокомиссию с бэкенда...');
      const response = await backendApiService.getCryptoCommission();
      
      if (response.success) {
        console.log('✅ Криптокомиссия загружена:', response.commissionPercentage);
        setCryptoCommissionPercentage(response.commissionPercentage || 4);
      } else {
        console.warn('⚠️ Ошибка загрузки криптокомиссии, используем значение по умолчанию');
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки криптокомиссии:', error);
    }
  }, []);

  // Загружаем курсы валют при монтировании компонента и подключаемся к SSE
  useEffect(() => {
    // При первой загрузке получаем курсы с backend
    loadCurrencyRates(false);
    
    // Загружаем криптокомиссию
    loadCryptoCommissionSettings();
    
    // Подключаемся к Server-Sent Events для мгновенных обновлений
    const eventSource = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/currency/events`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'rates' && data.rates) {
          console.log('🔄 Получены обновленные курсы через SSE:', data.rates);
          setCurrencyRates(data.rates);
          setRatesLastUpdated(new Date(data.timestamp));
        } else if (data.type === 'crypto_commission' && data.commissionPercentage !== undefined) {
          console.log('💰 Получена обновленная криптокомиссия через SSE:', data.commissionPercentage);
          setCryptoCommissionPercentage(data.commissionPercentage);
          // Пересчитываем цены мгновенно
          calculatePrices();
        } else if (data.type === 'markup_percentage' && data.markupPercentage !== undefined) {
          console.log('📊 Получена обновленная наценка через SSE:', data.markupPercentage);
          setMarkupPercentage(data.markupPercentage);
          // Пересчитываем цены мгновенно
          calculatePrices();
        }
      } catch (error) {
        console.error('❌ Ошибка обработки SSE:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('❌ Ошибка SSE соединения:', error);
    };
    
    // Очищаем соединение при размонтировании компонента
    return () => {
      eventSource.close();
    };
  }, []);

  // Расчет цен через бэкенд API без кэширования для мгновенных обновлений
  const calculatePrices = useCallback(async () => {
    try {
      // Строгая валидация на фронтенде
      if (!amountUSD || typeof amountUSD !== 'number' || isNaN(amountUSD) || !isFinite(amountUSD) || amountUSD < 2) {
        setLocalAmount(0);
        setRubAmount(0);
        return;
      }

      // Получаем цену в рублях с бэкенда
      const priceResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/currency/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amountUSD })
      });

      if (priceResponse.ok) {
        const priceData = await priceResponse.json();
        
        // Рассчитываем суммы в зависимости от типа оплаты
        let finalRubAmount = priceData.rubPrice;
        let cryptoAmountValue = 0;
        
        if (isCryptoPayment) {
          // Для криптооплаты: USD + комиссия из настроек
          cryptoAmountValue = Math.round((amountUSD * (1 + cryptoCommissionPercentage / 100)) * 100) / 100; // Округляем до 2 знаков
          finalRubAmount = 0; // Не показываем рубли для криптооплаты
        }
        
        setRubAmount(finalRubAmount);
        setCryptoAmount(cryptoAmountValue);
        
        // Сохраняем процент наценки с бэкенда
        if (priceData.markup !== undefined) {
          setMarkupPercentage(priceData.markup);
        }
        
        // Используем курсы из currencyRates (уже загружены через SSE)
        let localAmountValue = 0;
        if (currencyRates && selectedRegion && currencyRates[selectedRegion]) {
          // Для "Страны СНГ" (USD) показываем доллары как есть (1:1)
          localAmountValue = selectedRegion === 'USD' ? amountUSD : amountUSD * currencyRates[selectedRegion];
        }
        setLocalAmount(localAmountValue);
      } else {
        console.error('❌ Ошибка получения цены с бэкенда');
        setRubAmount(0);
        setLocalAmount(0);
      }
    } catch (error) {
      console.error('❌ Ошибка расчета цен:', error);
      setRubAmount(0);
      setLocalAmount(0);
    }
  }, [amountUSD, selectedRegion, currencyRates, isCryptoPayment, cryptoCommissionPercentage, markupPercentage]);

  // Обновляем цены при изменении USD суммы без дебаунсинга для мгновенных обновлений
  useEffect(() => {
    if (amountUSD > 0) {
      calculatePrices();
    }
  }, [amountUSD, selectedRegion, currencyRates, calculatePrices]);

  // Синхронизируем валюту аккаунта при изменении региона
  useEffect(() => {
    if (accountCurrencyAmount > 0 && currencyRates && selectedRegion !== 'USD') {
      // Пересчитываем USD сумму при изменении региона
      const newUsdAmount = convertAccountCurrencyToUSD(accountCurrencyAmount, selectedRegion);
      if (newUsdAmount !== amountUSD) {
        setAmountUSD(newUsdAmount);
      }
    }
  }, [selectedRegion, currencyRates, accountCurrencyAmount]);

  // Обновляем криптокомиссию при переключении на криптооплату
  useEffect(() => {
    if (isCryptoPayment) {
      loadCryptoCommissionSettings();
    }
  }, [isCryptoPayment, loadCryptoCommissionSettings]);

  const loadCurrencyRates = async (forceRefresh = false) => {
    try {
      const rates = await steamService.getCurrencyRatesWithCache(forceRefresh);
      setCurrencyRates(rates);
      setRatesLastUpdated(new Date());
    } catch (error) {
      console.error("❌ Ошибка загрузки курсов валют:", error);
      // При ошибке не устанавливаем курсы - покажем ошибку пользователю
      console.error('❌ Не удалось загрузить курсы валют');
      setCurrencyRates(null);
    }
  };


  const validateSteamAccount = async () => {
    if (!steamAccount.trim()) {
      setAccountValidation({
        isValid: false,
        message: "Введите логин Steam аккаунта"
      });
      return;
    }

    // Проверяем минимальную сумму
    if (amountUSD < 2) {
      setAccountValidation({
        isValid: false,
        message: "Минимальная сумма для пополнения: $2"
      });
      return;
    }

    // Проверяем максимальную сумму
    if (amountUSD > 350) {
      setAccountValidation({
        isValid: false,
        message: "Максимальная сумма для пополнения: $350"
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await steamService.verifySteamAccount(steamAccount);
      setAccountValidation(result);
    } catch (error) {
      setAccountValidation({
        isValid: false,
        message: "Ошибка проверки аккаунта"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    setIsLoading(true);
    try {
      // Переходим к странице оплаты СБП (orderId будет сгенерирован на бэкенде)
      setStep(3);
    } catch (error) {
      alert("Ошибка создания платежа");
    } finally {
      setIsLoading(false);
    }
  };

  const regions = [
    { code: "RUB", name: "Россия", currency: "₽" },
    { code: "KZT", name: "Казахстан", currency: "₸" },
    { code: "USD", name: "Страны СНГ", currency: "$" }
  ];

  const handlePaymentSuccess = (result) => {
    console.log('✅ Платеж успешен, данные:', result);
    // Сохраняем информацию о заказе для отображения
    setOrderInfo({
      orderId: result.orderId || result.order_id,
      steamAccount: result.steamAccount || steamAccount,
      amountUSD: result.amountUSD || amountUSD,
      message: result.message || 'Steam аккаунт будет пополнен в ближайшее время'
    });
    setStep(2); // Показываем экран успеха
  };

  const handlePaymentFail = (error) => {
    alert(`Ошибка оплаты: ${error}`);
    setStep(1); // Возвращаемся к основному интерфейсу
  };

  const handleBackToMain = () => {
    setStep(1);
  };

  // Обработчик изменения суммы USD с правильной валидацией
  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Разрешаем пустую строку для возможности удаления
    if (value === '') {
      setAmountUSD(0);
      // Очищаем поле валюты аккаунта при работе с USD
      setAccountCurrencyAmount(0);
      // Очищаем отображение сумм
      setLocalAmount(0);
      setRubAmount(0);
      return;
    }
    
    // Нормализуем ввод: заменяем запятую на точку
    value = value.replace(',', '.');
    
    // Проверяем базовый формат: только цифры, точка и максимум 2 знака после точки
    const decimalRegex = /^\d+(\.\d{0,2})?$/;
    if (!decimalRegex.test(value)) {
      // Если формат неправильный, не обновляем состояние
      // Это предотвращает ввод более 2 знаков после точки
      return;
    }
    
    // Проверяем, что это число
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // Ограничиваем максимальную сумму
      if (numValue > 350) {
        setAmountUSD(350);
      } else {
        // Ограничиваем до 2 знаков после точки
        const roundedValue = Math.round(numValue * 100) / 100;
        setAmountUSD(roundedValue);
      }
      // Очищаем поле валюты аккаунта при работе с USD
      setAccountCurrencyAmount(0);
    }
  };

  // Обработчик изменения суммы в валюте аккаунта
  const handleAccountCurrencyAmountChange = (e) => {
    let value = e.target.value;
    
    // Разрешаем пустую строку для возможности удаления
    if (value === '') {
      setAccountCurrencyAmount(0);
      setAmountUSD(0);
      return;
    }
    
    // Нормализуем ввод: заменяем запятую на точку
    value = value.replace(',', '.');
    
    // Проверяем базовый формат: только цифры, точка и максимум 2 знака после точки
    const decimalRegex = /^\d+(\.\d{0,2})?$/;
    if (!decimalRegex.test(value)) {
      return;
    }
    
    // Проверяем, что это число
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setAccountCurrencyAmount(numValue);
      
      // Конвертируем в USD с округлением в большую сторону
      const usdAmount = convertAccountCurrencyToUSD(numValue, selectedRegion);
      
      // Ограничиваем максимальную сумму USD
      if (usdAmount > 350) {
        setAmountUSD(350);
        // Пересчитываем обратно для точности
        const maxAccountAmount = convertUSDToAccountCurrency(350, selectedRegion);
        setAccountCurrencyAmount(maxAccountAmount);
      } else {
        setAmountUSD(usdAmount);
      }
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Основной интерфейс - все поля сразу видны */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Steam для пополнения
            </label>
            <div className="relative">
              <input
                type="text"
                value={steamAccount}
                onChange={(e) => setSteamAccount(e.target.value)}
                placeholder="Введите логин Steam"
                disabled={accountValidation?.isValid}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  accountValidation?.isValid 
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 cursor-not-allowed' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                }`}
              />
              {accountValidation?.isValid && (
                <button
                  onClick={() => {
                    setAccountValidation(null);
                    setSteamAccount('');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 transition-colors"
                  title="Изменить логин"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            <div className="mt-1">
              <span
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('how-to-find-login');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    console.warn('Элемент #how-to-find-login не найден');
                  }
                }}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer inline-block"
              >
                что такое логин?***
              </span>
            </div>
          </div>

          {accountValidation && (
            <div className={`p-3 rounded-md ${
              accountValidation.isValid 
                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
            }`}>
              <div className="flex items-center">
                {accountValidation.isValid ? (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{accountValidation.message}</span>
              </div>
              {accountValidation.steamId && (
                <div className="text-xs mt-1 opacity-75">
                  Steam ID: {accountValidation.steamId}
                </div>
              )}
            </div>
          )}

          <button
            onClick={validateSteamAccount}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            {isLoading ? "Проверяем..." : "Подтвердить"}
          </button>

          {/* Выбор региона */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Выберите регион
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {regions.map(region => (
                <option key={region.code} value={region.code}>
                  {region.name} ({region.currency})
                </option>
              ))}
            </select>
          </div>

          {/* Выбор суммы */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Сумма пополнения (USD)
            </label>
            <input
              type="number"
              value={amountUSD || ''}
              onChange={handleAmountChange}
              max="350"
              step="0.01"
              placeholder="Введите сумму (2-350 USD)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Минимальная сумма: $2, Максимальная: $350
            </div>

            {/* Калькулятор валют аккаунта */}
            {currencyRates && selectedRegion !== 'USD' && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Или введите сумму в валюте аккаунта:
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={accountCurrencyAmount || ''}
                    onChange={handleAccountCurrencyAmountChange}
                    step="0.01"
                    placeholder={`Введите сумму в ${regions.find(r => r.code === selectedRegion)?.currency}`}
                    className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-800/30 dark:text-white text-sm"
                  />
                  <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                    {regions.find(r => r.code === selectedRegion)?.currency}
                  </span>
                </div>
              </div>
            )}

            {!currencyRates && (
              <div className="text-yellow-500 text-sm mt-1">
                Загрузка курсов валют...
              </div>
            )}
            {amountUSD > 0 && amountUSD < 2 && (
              <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-md">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-yellow-800 dark:text-yellow-200">
                    Минимальная сумма для пополнения: $2
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Информация о платеже */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
            <div className="text-center space-y-2">
              
              {currencyRates ? (
                <>
                  <div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-white">
                      В Steam вы получите:
                    </div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      ${amountUSD.toFixed(2)} ≈ {localAmount.toFixed(2)} {regions.find(r => r.code === selectedRegion)?.currency}{selectedRegion === 'USD' ? '' : '*'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedRegion === 'USD' 
                        ? 'Курс: 1 USD = 1 USD (фиксированный)'
                        : `Курс: 1 USD = ${currencyRates[selectedRegion]} ${regions.find(r => r.code === selectedRegion)?.currency}*`
                      }
                    </div>
                  </div>
                  <div className="border-t pt-2">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Стоимость услуги:
                    </div>
                    <div className={`text-lg font-bold ${isCryptoPayment 
                      ? 'text-purple-600 dark:text-purple-400' 
                      : 'text-green-600 dark:text-green-400'
                    }`}>
                      {isCryptoPayment ? `${cryptoAmount} USDT` : `${rubAmount} ₽`}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {isCryptoPayment 
                        ? `(${amountUSD} USD + ` : `(курс USD/RUB** + ${markupPercentage}% комиссия)`
                      }
                      {isCryptoPayment && (
                        <>
                          <span className="text-purple-600 dark:text-purple-400 font-medium">{cryptoCommissionPercentage}% комиссия</span>)
                        </>
                      )}
                    </div>
                  </div>
                  {ratesLastUpdated && (
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Курсы обновлены: {ratesLastUpdated.toLocaleTimeString('ru-RU')}
                        </div>
                        <button
                          onClick={() => loadCurrencyRates(true)}
                          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                        >
                          Обновить
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Загрузка курсов валют...
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Пожалуйста, подождите
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Кнопка оплаты */}
          <button
            onClick={handleCreatePayment}
            disabled={isLoading || amountUSD < 2 || !accountValidation?.isValid || !currencyRates}
            className={`w-full ${isCryptoPayment 
              ? 'bg-purple-600 hover:bg-purple-700' 
              : 'bg-green-600 hover:bg-green-700'
            } disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition duration-200`}
          >
            {isLoading ? "Создаем платеж..." : 
             amountUSD < 2 ? "Минимальная сумма $2" : 
             !accountValidation?.isValid ? "Подтвердите Steam аккаунт" :
             !currencyRates ? "Загрузка курсов..." :
             isCryptoPayment ? `Оплатить ${cryptoAmount} USDT` :
             `Оплатить ${rubAmount} ₽`}
          </button>

          {/* Переключатель криптооплаты - маленький текст под кнопкой */}
          <div className="mt-2 text-center">
            <label className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isCryptoPayment}
                onChange={(e) => setIsCryptoPayment(e.target.checked)}
                className="w-3 h-3 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-1 dark:bg-gray-700 dark:border-gray-600 mr-1"
              />
              <span>Оплата криптой <span className="text-purple-600 dark:text-purple-400 font-medium">комиссия {cryptoCommissionPercentage}%</span></span>
            </label>
          </div>
        </div>
      )}

      {/* Шаг 3: Страница оплаты СБП */}
      {step === 3 && (
        <SBPPaymentPage
          amount={isCryptoPayment ? cryptoAmount : rubAmount}
          amountUSD={amountUSD}
          steamAccount={steamAccount}
          isCryptoPayment={isCryptoPayment}
          cryptoAmount={cryptoAmount}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFail={handlePaymentFail}
          onBack={handleBackToMain}
        />
      )}

      {/* Шаг 2: Экран успеха */}
      {step === 2 && (
        <div className="space-y-4 text-center">
          <div className="text-green-600 dark:text-green-400">
            <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">Платеж успешен!</h3>
            
            {/* Информация о заказе */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Steam аккаунт:</span> {orderInfo?.steamAccount || steamAccount}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Будет пополнен на:</span> <span className="font-bold text-blue-600">${orderInfo?.amountUSD || amountUSD}</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Сумма к оплате:</span> 
                <span className={isCryptoPayment 
                  ? 'text-purple-600 dark:text-purple-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400'
                }>
                  {isCryptoPayment ? ` ${cryptoAmount} USDT` : ` ${rubAmount} ₽`}
                </span>
              </p>
              {orderInfo?.orderId && (
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  <span className="font-medium">Номер заказа:</span> {orderInfo.orderId}
                </p>
              )}
            </div>

            {/* Информационное сообщение */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-left">
                  <p className="text-blue-800 dark:text-blue-200 text-sm font-medium mb-1">
                    {orderInfo?.message || 'Steam аккаунт будет пополнен в ближайшее время'}
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs">
                    Если в течение часа пополнения не будет, сообщите номер заказа <span className="font-mono font-bold">{orderInfo?.orderId || 'XXXXX'}</span> в техподдержку: <a href="https://t.me/tp_trendy_eng_shop" className="underline hover:no-underline">@tp_trendy_eng_shop</a>
                  </p>
                </div>
              </div>
            </div>
          </div>


          <button
            onClick={handleBackToMain}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            Создать новый платеж
          </button>
        </div>
      )}
    </div>
  );
};

export default SteamTopUpWidget;
